import toast from "react-hot-toast";
import { ReportAgreement } from "../../../../components/dashboard/service/reports/report-agreement";
import { ReportAccessesSection } from "../../../../components/dashboard/service/reports/ReportAccessesSection";
import { useDispatch } from "react-redux";
import { useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ServiceReportOperationObjectStatus, getChecklistStatus } from "../../../../components/dashboard/service/reports/utils";
import { fetchReport, fetchReportChecklist } from "../../../../api/service/report";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { ReportAddress } from "../../../../components/dashboard/service/reports/report-address";
import {
  FullReportAddress,
  FullReportTitle,
  ReportAppointmentApprovedStatus,
  ReportInvoiceStatus,
  ReportMustBeRectified,
  ReportStatus,
} from "../../../../components/dashboard/service/reports/utils";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import {
  ArrowPathRoundedSquareIcon,
  AtSymbolIcon,
  BuildingOfficeIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  MapPinIcon,
  PencilIcon,
  PrinterIcon,
  UsersIcon,
  WrenchIcon,
  PhoneIcon,
  WrenchScrewdriverIcon,
  UserIcon,
  CubeIcon,
  VariableIcon,
  ClipboardDocumentIcon,
  CalculatorIcon,
  ClockIcon,
  SparklesIcon,
  RectangleGroupIcon,
} from "@heroicons/react/24/outline";
import ServiceReportEditModal from "../../../../components/dashboard/service/reports/service-report-edit-modal";
import { ReportResetDialog } from "../../../../components/dashboard/service/reports/report-reset-dialog";
import { ReportOrderDialog } from "../../../../components/dashboard/service/reports/report-order-dialog";
import { ReportPrintDialog } from "../../../../components/dashboard/service/reports/report-print-dialog";
import { ReportSendDialog } from "../../../../components/dashboard/service/reports/report-send-dialog";
import {
  CopyToClipboard,
  TWclassNames,
} from "../../../../components/utils/Div";
import HistoriesModal from "../../../../components/dashboard/histories/modal";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { ReportCopyModal } from "../../../../components/dashboard/service/reports/report-copy-moda";
import {
  mapsServiceUrl,
  serviceAgreementPath,
  stripHtml,
} from "../../../../components/Utils";
import { ReportExecutiveDialog } from "../../../../components/dashboard/service/reports/report-executive";
import { MentionsSection } from "../../../../components/dashboard/module_tags/mentionsSection";
import { ReportRectifyDisplay } from "../../../../components/dashboard/service/reports/report-rectify-display";
import { ReportApprovalSection } from "../../../../components/dashboard/service/reports/report-approval-section";
import { NotesSection } from "../../../../components/dashboard/notes/notesSection";
import { CustomerSystemSection } from "../../../../components/dashboard/customers/systems/customerSystemSection";
import { ServiceReportTypeConditionsDialog } from "../../../../components/dashboard/service/settings/service-report-type-conditions-dialog";
import { useSelector } from "react-redux";
import { CommuncationsSection } from "../../../../components/dashboard/communcations/communcationsSection";
import { LabelPrintDialog } from "../../../../components/utils/labelPrintDialog";
import { TinyDocumentSection } from "../../../../components/dashboard/documents/tinyDocumentSection";
import { TimeRegistrationSection } from "../../../../components/dashboard/hours/time-registration-section";
import { ReportSectionCard } from "../../../../components/dashboard/service/reports/report-section-card";
import { ReportSectionDialog } from "../../../../components/dashboard/service/reports/report-section-dialog";
import { DocumentManagerV6 } from "../../../../components/dashboard/Media/DocumentManagerV6";
import ReportFinishDialog from "../../../../components/dashboard/service/reports/report-finish-dialog";
import { ContactSection } from "../../../../components/dashboard/projects/ContactSection";
import { ReportAdditionalWorkCalculator } from "../../../../components/dashboard/service/reports/report-additional-work-calculator";
import {
  ReportSummarySection,
  useServiceReportFinancialSummary,
} from "../../../../components/dashboard/service/reports/report-summary-section";
import { ReportAdminMenu } from "../../../../components/dashboard/service/reports/report-admin-menu";
import { ReportDevMenu } from "../../../../components/dashboard/service/reports/report-dev-menu";
import { ItemListProductStatusTag } from "../../../../components/dashboard/service/reports/item-list-product-status-tag";
import { PMDialog } from "../../../../components/utils/PMDialog";
import ReportGuideOnboardingDialog from "../../../../components/dashboard/service/reports/ReportGuideOnboardingDialog";
import ReportGuideHistoryDialog from "../../../../components/dashboard/service/reports/ReportGuideHistoryDialog";
import {
  createReportGuideRun,
  fetchReportGuideRuns,
  fetchReportGuideTemplates,
} from "../../../../api/reportGuides";
import { excludingPromangStockReportGuideTemplates } from "../../../../utils/promangStockReportGuide";
import {
  reportVariablesPagePath,
  shouldOpenLegacyVariablesDialog,
} from "../../../../utils/reportVariablesUi";

export const ReportViewV2 = () => {
  const { t } = useTranslation();
  const settings = useSelector((state) => state.session.currentUser.settings);
  const navigate = useNavigate();

  const { id } = useParams();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    report: {},
  });


  const { report, isLoading, error, errors } = itemState;

  const reportVariablesHref = useMemo(
    () => (report?.id ? reportVariablesPagePath(report.id) : null),
    [report?.id]
  );

  const useLegacyVariablesSetting = settings?.use_legacy_variables || false;
  const variablesOpenLegacyDialog = useMemo(
    () => shouldOpenLegacyVariablesDialog(report, useLegacyVariablesSetting),
    [report, useLegacyVariablesSetting]
  );

  const {
    summary: reportFinancialSummary,
    loading: reportFinancialSummaryLoading,
  } = useServiceReportFinancialSummary(id, report?.updated_at);

  
  const [checklist, setChecklist] = useState(null);

  const hasAdminAccess = useUserHasAccess({ role: "service", action: "reset" });

  const reportListFields = useMemo(() => {
    const editAttrs = hasAdminAccess
      ? ""
      : ",user_can_edit_with_rights,user_can_edit";
    return `*,item_list_product_status,contact.*,-trips,-text_messages,-activity_items,-items,checklist${editAttrs},approved,not_approved,updated_after_process,process_note,approval_status`;
  }, [hasAdminAccess]);

  const reportListFieldsWithPdf = useMemo(() => {
    const editAttrs = hasAdminAccess
      ? ""
      : ",user_can_edit_with_rights,user_can_edit";
    return `*,item_list_product_status,contact.*,-trips,-text_messages,pdf_customer_report_url,-activity_items,-items,checklist${editAttrs},approved,not_approved,updated_after_process,process_note,approval_status`;
  }, [hasAdminAccess]);

  // Helper function to fetch checklist
  // const loadChecklist = useCallback((reportId) => {
  //   if (!reportId) {
  //     setChecklist(null);
  //     return;
  //   }

  //   fetchReportChecklist(reportId, {
  //     fields: "id,title,description,status,is_items_editable,items.id,items.title,items.isApproved,items.isDeviation,items.description,items.row_order",
  //   })
  //     .then((checklistResponse) => {
  //       // Check if response has errors (from backend)
  //       if (checklistResponse?.errors) {
  //         // If 404 error, it means no checklist exists yet - that's fine
  //         if (checklistResponse.errors.some(err => err.includes("does not have a checklist") || err.includes("404"))) {
  //           setChecklist(null);
  //         } else {
  //           console.error("Error loading checklist:", checklistResponse.errors);
  //           setChecklist(null);
  //         }
  //         return;
  //       }
        
  //       // Response can be either response.data (from axios) or just data (from our wrapper)
  //       const checklistData = checklistResponse?.data || checklistResponse;
  //       if (checklistData && checklistData.id) {
  //         // Ensure items is always an array
  //         if (!checklistData.items || !Array.isArray(checklistData.items)) {
  //           checklistData.items = [];
  //         }
  //         console.log("Checklist loaded:", checklistData.id, "items:", checklistData.items?.length || 0);
  //         setChecklist(checklistData);
  //       } else {
  //         console.log("No checklist data found");
  //         setChecklist(null);
  //       }
  //     })
  //     .catch((error) => {
  //       console.error("Error loading checklist:", error);
  //       // If 404, it means no checklist exists yet - that's fine
  //       if (error?.response?.status === 404 || error?.status === 404) {
  //         setChecklist(null);
  //       } else {
  //         setChecklist(null);
  //       }
  //     });
  // }, []);

  // Helper function to get finish report progress using same method as report-finish-dialog.js
  const getFinishReportProgress = () => {
    const finishReportOperations =
      report?.operations?.filter((op) => op.name === "finish_report") || [];

    const completedOperations = finishReportOperations.filter(
      (op) => op.can_be_completed === true
    );

    return {
      completed: completedOperations.length,
      total: finishReportOperations.length,
      isComplete:
        completedOperations.length === finishReportOperations.length &&
        finishReportOperations.length > 0,
    };
  };

  const handleDocumentsChange = (documents) => {
    // Document count is now managed by DocumentManagerV6
    // This callback is kept for potential future use
  };

  const refreshTimeout = useRef();

  useEffect(() => {
    if (!id) return;

    // Reset state when id changes
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: false,
      errors: [],
      report: {},
    }));

    const params = {
      fields: reportListFields,
    };

    // Fetch report
    fetchReport(id, params)
      .then((response) => {
        // Check if response contains errors (even with 200 status)
        const data = response?.data || response;
        
        if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          // Response has errors array - treat as error
          console.log('report errors in response', data.errors);
          toast.error(data.errors[0] || "Something went wrong when trying to load the report");
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            error: true,
            errors: data.errors,
          }));
          return;
        }
        
        if (data?.error) {
          // Response has error field - treat as error
          const errorMessage = Array.isArray(data.error) ? data.error : [data.error];
          console.log('report error in response', errorMessage);
          toast.error(errorMessage[0] || "Something went wrong when trying to load the report");
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            error: true,
            errors: errorMessage,
          }));
          
          return;
        }

        // Success - set report data
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          report: data?.data || data,

        }));

        setChecklist(data?.data?.checklist || data?.checklist || null);


        // Checklist will be loaded by useEffect when report.checklist_id is available
      })
      .catch((err) => {
        console.log('report catch error', err);
        const errorMessage = err?.errors
          ? (Array.isArray(err.errors) ? err.errors : [err.errors])
          : err?.response?.data?.errors 
          ? (Array.isArray(err.response.data.errors) ? err.response.data.errors : [err.response.data.errors])
          : err?.response?.data?.error
          ? (Array.isArray(err.response.data.error) ? err.response.data.error : [err.response.data.error])
          : err?.error
          ? (Array.isArray(err.error) ? err.error : [err.error])
          : err?.message
          ? [err.message]
          : ["Noe gikk galt ved lasting av rapport"];

        console.log('errorMessage', errorMessage);
        
        toast.error(errorMessage[0] || "Something went wrong when trying to load the report");
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          error: true,
          errors: errorMessage,
        }));
      });

    // Cleanup function to clear timeout when component unmounts or id changes
    return () => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }
    };
  }, [id, reportListFields]);

  // // Load checklist when report changes and has checklist.id
  // useEffect(() => {
  //   const checklistId = report?.checklist?.id;
  //   if (report?.id && checklistId) {
  //     // Only load if we don't have checklist or if checklist ID changed
  //     if (!checklist || checklist.id !== checklistId) {
  //       console.log("Loading checklist for report:", report.id, "checklist.id:", checklistId);
  //       loadChecklist(report.id);
  //     }
  //   } else if (report?.id && !checklistId) {
  //     // Clear checklist if report doesn't have checklist.id
  //     console.log("Report has no checklist.id, clearing checklist");
  //     setChecklist(null);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [report?.id, report?.checklist?.id]);

  const handleOnRefresh = useCallback((data) => {
    clearTimeout(refreshTimeout.current);

    if (!data || typeof data !== "object" || !("id" in data)) {
      return;
    }

    let didApply = false;
    setItemState((prevState) => {
      if (data.id !== prevState.report?.id) {
        return prevState;
      }

      const prev = prevState.report;
      const prevAt = prev?.updated_at ? Date.parse(prev.updated_at) : NaN;
      const incAt = data.updated_at ? Date.parse(data.updated_at) : NaN;
      const incomingIsOlder =
        Number.isFinite(prevAt) &&
        Number.isFinite(incAt) &&
        incAt < prevAt;
      const keepRichText =
        incomingIsOlder &&
        (data.report !== undefined || data.internal_report !== undefined);

      const updatedReport = {
        ...prev,
        ...data,
        items: data.items !== undefined ? data.items : prev?.items,
        items_summary:
          data.items_summary !== undefined ? data.items_summary : prev?.items_summary,
        variables:
          data.variables !== undefined ? data.variables : prev?.variables,
        checklist:
          data.checklist !== undefined ? data.checklist : prev?.checklist,
        report: keepRichText
          ? prev?.report
          : data.report !== undefined
            ? data.report
            : prev?.report,
        internal_report: keepRichText
          ? prev?.internal_report
          : data.internal_report !== undefined
            ? data.internal_report
            : prev?.internal_report,
        operations: data.operations || prev?.operations,
        updated_at: keepRichText
          ? prev?.updated_at
          : data.updated_at || prev?.updated_at,
        approved: data.approved !== undefined ? data.approved : prev?.approved,
        not_approved:
          data.not_approved !== undefined ? data.not_approved : prev?.not_approved,
        updated_after_process:
          data.updated_after_process !== undefined
            ? data.updated_after_process
            : prev?.updated_after_process,
        process_note:
          data.process_note !== undefined ? data.process_note : prev?.process_note,
        approval_status: data.approval_status || prev?.approval_status,
        additional_work_calculator_data:
          data.additional_work_calculator_data !== undefined
            ? data.additional_work_calculator_data
            : prev?.additional_work_calculator_data,
        item_list_product_status:
          data.item_list_product_status !== undefined
            ? data.item_list_product_status
            : prev?.item_list_product_status,
        participants:
          data.participants !== undefined ? data.participants : prev?.participants,
      };

      didApply = true;
      return {
        ...prevState,
        report: updatedReport,
      };
    });

    if (didApply) {
      setReportVersion((v) => v + 1);
    }
  }, []);

  const handleParticipantsChange = useCallback((list) => {
    setItemState((prev) => ({
      ...prev,
      report: prev.report?.id
        ? { ...prev.report, participants: list }
        : prev.report,
    }));
  }, []);

  const canEditReportContent = useMemo(
    () =>
      hasAdminAccess ||
      report?.user_can_edit_with_rights !== false ||
      report?.user_can_edit !== false,
    [hasAdminAccess, report?.user_can_edit_with_rights, report?.user_can_edit]
  );

  // Memoize the report object to prevent unnecessary re-renders
  const memoizedReport = useMemo(() => {
    return report;
  }, [
    report?.id,
    report?.updated_at,
    report?.report,
    report?.internal_report,
    report?.variables?.length,
    JSON.stringify(report?.items_summary),
    report?.checklist?.id, // Only track if checklist exists, not full checklist data



    // Add dependency on variables content to detect changes
    
    JSON.stringify(
      report?.checklist?.items?.map((v) => ({
        id: v.id,
        isDeviation: v.isDeviation,
        isApproved: v.isApproved,
        is_not_applicable: v.is_not_applicable,
        response_code: v.response_code,
        note: v.note,
        checklist_type_id: v.checklist_type_id,
        title: v.title,
        description: v.description,
      }))
    ),
    JSON.stringify(
      report?.variables?.map((v) => ({
        id: v.id,
        reference: v.reference,
        field1: v.field1,
        field2: v.field2,
        field3: v.field3,
        field4: v.field4,
        note: v.note,
        unit_id: v.unit_id,
        variable_type_id: v.variable_type_id,
        delete: v.delete,
      }))
    ),
    JSON.stringify(report?.additional_work_calculator_data),
    // Checklist items are now loaded separately, so we only track if checklist exists
  ]);

  // Memoize the report ID to prevent unnecessary re-renders of NotesSection
  const reportId = useMemo(() => report?.id, [report?.id]);

  // Memoize contact ID to prevent unnecessary re-renders of ContactSection
  // Extract the ID value directly to avoid re-renders when contact object reference changes
  const contactId = useMemo(() => {
    return report?.contact?.id ?? null;
  }, [report?.contact?.id]);

  const reportSectionContentPreview = useMemo(() => {
    const plain = stripHtml(report?.report || "").trim();
    if (!plain) return "";
    return plain.length > 160 ? `${plain.slice(0, 159)}…` : plain;
  }, [report?.report]);

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportResetDialogOpen, setReportResetDialogOpen] = useState(false);
  const [reportExecutiveDialogOpen, setReportExecutiveDialogOpen] =
    useState(false);
  const [reportOrderDialogOpen, setReportOrderDialogOpen] = useState(false);
  const [reportPrintDialogOpen, setReportPrintDialogOpen] = useState(false);
  const [reportSendDialogOpen, setReportSendDialogOpen] = useState(false);
  const [reportCopyDialogOpen, setReportCopyDialogOpen] = useState(false);
  const [labelPrintDialogOpen, setLabelPrintDialogOpen] = useState(false);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [reportFinishDialogOpen, setReportFinishDialogOpen] = useState(false);
  const [reportVersion, setReportVersion] = useState(0);
  const [calculatorDialogOpen, setCalculatorDialogOpen] = useState(false);
  const [headerHistoriesOpen, setHeaderHistoriesOpen] = useState(false);
  const [headerCommunicationsOpen, setHeaderCommunicationsOpen] =
    useState(false);
  const [timeRegistrationDialogSignal, setTimeRegistrationDialogSignal] =
    useState(0);
  const [reportGuideTemplates, setReportGuideTemplates] = useState([]);
  const [reportGuideState, setReportGuideState] = useState({
    open: false,
    template: null,
    isSaving: false,
  });
  const [reportGuideChooserState, setReportGuideChooserState] = useState({
    open: false,
    templates: [],
  });
  const [reportGuideRuns, setReportGuideRuns] = useState([]);
  const [reportGuideHistoryOpen, setReportGuideHistoryOpen] = useState(false);

  useEffect(() => {
    const reportTypeId = report?.report_type?.id || report?.report_type_id || report?.report_type;
    if (!report?.id || !reportTypeId) return;

    let mounted = true;
    fetchReportGuideTemplates({
      report_type_id: reportTypeId,
      purpose: "field_report",
    })
      .then((templates) => {
        if (!mounted) return;
        const availableTemplates = excludingPromangStockReportGuideTemplates(
          Array.isArray(templates) ? templates.filter((template) => template?.id) : []
        );
        setReportGuideTemplates(availableTemplates);
      })
      .catch(() => setReportGuideTemplates([]));

    return () => {
      mounted = false;
    };
  }, [report?.id, report?.report_type?.id, report?.report_type_id, report?.report_type]);

  useEffect(() => {
    if (!report?.id) {
      setReportGuideRuns([]);
      return;
    }

    let mounted = true;
    fetchReportGuideRuns(report.id).then((runs) => {
      if (!mounted) return;
      setReportGuideRuns(Array.isArray(runs) ? runs : []);
    });

    return () => {
      mounted = false;
    };
  }, [report?.id, report?.updated_at]);

  const showReportGuideSection = useMemo(
    () => reportGuideTemplates.length > 0 || reportGuideRuns.length > 0,
    [reportGuideTemplates.length, reportGuideRuns.length]
  );

  function startReportGuide(template) {
    if (!template?.id) return;
    setReportGuideState({
      open: true,
      template,
      isSaving: false,
    });
  }

  function closeReportGuide() {
    setReportGuideState({
      open: false,
      template: null,
      isSaving: false,
    });
  }

  function openReportGuideChooser() {
    if (reportGuideTemplates.length === 0) {
      toast.error(t("Ingen rapportguide er tilgjengelig"));
      return;
    }
    if (reportGuideTemplates.length === 1) {
      startReportGuide(reportGuideTemplates[0]);
      return;
    }
    setReportGuideChooserState({ open: true, templates: reportGuideTemplates });
  }

  function closeReportGuideChooser() {
    setReportGuideChooserState({ open: false, templates: [] });
  }

  function openGuideEntryPoint() {
    if (reportGuideTemplates.length > 0) {
      openReportGuideChooser();
      return;
    }
    setReportGuideHistoryOpen(true);
  }

  async function completeReportGuide(guideRun) {
    if (!report?.id) return;

    setReportGuideState((state) => ({ ...state, isSaving: true }));
    const response = await createReportGuideRun(report.id, {
      report_guide_template_id: guideRun.report_guide_template_id,
      purpose: guideRun.purpose,
      answers: guideRun.answers,
      requested_from_customer: false,
    });

    if (response?.errors) {
      toast.error(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors));
      setReportGuideState((state) => ({ ...state, isSaving: false }));
      return;
    }

    toast.success(t("Rapportguide lagret"));
    closeReportGuide();
    handleOnRefresh(response?.report || response?.data);
    fetchReportGuideRuns(report.id).then((runs) => {
      setReportGuideRuns(Array.isArray(runs) ? runs : []);
    });
  }

  const headerMeta = [
    {
      component: <ReportStatus report={report} />,
      label: "Status",
      hidden: false,
    },
    {
      component: <ReportAppointmentApprovedStatus report={report} />,
      label: "AppointmentStatus",
      hidden: ["new", "ongoing"].includes(report.status) ? false : true,
    },
    {
      component: <ReportMustBeRectified rectify={report?.must_be_rectified} />,
      label: "MustBeRectified",
      hidden: ["accomplished"].includes(report.status) ? false : true,
    },
    {
      label: "Rapport sendt",
      icon: CheckBadgeIcon,
      textColor: "text-green-500",
      iconColor: "text-green-500",
      hidden: report?.reportIsSent ? false : true,
    },
    {
      component: <ReportInvoiceStatus report={report} />,
      label: "Invoice Status",
      hidden: report?.invoiced ? false : true,
    },
    {
      component: (
        <ItemListProductStatusTag status={report?.item_list_product_status} />
      ),
      label: "ItemListHandling",
      hidden: !(report?.item_list_product_status && report.item_list_product_status.lines_total > 0),
    },
  ];

  const summaryItems = useMemo(() => {
    const customer = report?.customer;
    const customerId = customer?.id;
    const customerPath = customerId
      ? `/dashboard/customers/${customerId}`
      : null;
    const titleText = customer?.displayTitle || "—";

    const customerValue = (
      <div className="space-y-1.5">
        <div>
          {customerPath ? (
            <Link
              to={customerPath}
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {titleText}
            </Link>
          ) : (
            titleText
          )}
        </div>
        {(customer?.mobile || customer?.email) && (
          <div className="space-y-1 text-xs font-normal text-gray-600">
            {customer?.mobile ? (
              <a
                href={`tel:${String(customer.mobile).replace(/\s/g, "")}`}
                className="flex min-w-0 items-start gap-1.5 break-all text-gray-600 hover:text-blue-700"
              >
                <PhoneIcon
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400"
                  aria-hidden
                />
                <span>{customer.mobile}</span>
              </a>
            ) : null}
            {customer?.email ? (
              <a
                href={`mailto:${customer.email}`}
                className="flex min-w-0 items-start gap-1.5 break-all text-gray-600 hover:text-blue-700"
              >
                <AtSymbolIcon
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400"
                  aria-hidden
                />
                <span>{customer.email}</span>
              </a>
            ) : null}
          </div>
        )}
      </div>
    );

    return [
      {
        label: t("report.view.summary.customer"),
        value: customerValue,
        path: null,
      },
      {
        label: "Prosjekt",
        value: report?.project?.displayTitle || "-",
        path: report?.project?.id
          ? `/dashboard/projects/${report.project.id}`
          : null,
        hidden: !report?.project?.id,
      },
      {
        label: "Utførende",
        value: report?.executing_employee?.name || "-",
      },
      {
        label: "Rapporttype",
        value: report?.report_type?.displayTitle || "-",
      },
    ];
  }, [report?.customer, report?.project, report?.executing_employee, report?.report_type, t]);

  const headerActions = [
    {
      component: null,
      label: t("histories.title"),
      description: "",
      path: null,
      action: () => setHeaderHistoriesOpen(true),
      icon: ClockIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !hasAdminAccess,
      menu: true,
    },
    {
      component: null,
      label: t("Communication"),
      description: "",
      path: null,
      action: () => setHeaderCommunicationsOpen(true),
      icon: RectangleGroupIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !hasAdminAccess || !report?.customer?.id,
      menu: true,
    },

    {
      component: null,
      label: "Utførende",
      description: "",
      path: null,
      action: () => setReportExecutiveDialogOpen(true),
      icon: UserIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: hasAdminAccess ? false : true,
      menu: true,
    },
    {
      component: null,
      label: "Reset",
      description: "",
      path: null,
      action: () => setReportResetDialogOpen(true),
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: hasAdminAccess ? false : true,
      menu: true,
    },
    {
      component: null,
      label: "Order",
      description: "",
      path: null,
      action: () => setReportOrderDialogOpen(true),
      icon: ClipboardDocumentListIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: report?.finished ? false : true,
      menu: true,
    },
    {
      component: null,
      label: "Copy",
      description: "",
      path: null,
      action: () => setReportCopyDialogOpen(true),
      icon: ClipboardDocumentCheckIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    {
      component: null,
      label: "Skriv ut",
      description: "",
      path: null,
      action: () => setReportPrintDialogOpen(true),
      icon: PrinterIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    {
      component: null,
      label: "Last ned kunderapport",
      description: "",
      path: null,
      action: () => {
        if (report?.pdf_customer_report_url) {
          window.open(report.pdf_customer_report_url, "_blank");
        } else {
          toast.error("Kunderapport PDF er ikke tilgjengelig. Generer den via 'Skriv ut'.");
        }
      },
      icon: ClipboardDocumentCheckIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !report?.pdf_customer_report_url || !report?.approved,
      menu: true,
    },
    {
      component: null,
      label: "Send",
      description: "",
      path: null,
      action: () => setReportSendDialogOpen(true),
      icon: AtSymbolIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    {
      component: null,
      label: t("report.view.mobileNav.guide"),
      description: "",
      path: null,
      action: openGuideEntryPoint,
      icon: SparklesIcon,
      textColor: "text-sky-700",
      bgColor: "bg-white",
      hidden: !showReportGuideSection,
      menu: true,
    },
    {
      component: null,
      label: "Kalkyle",
      description: "",
      path: null,
      action: () => setCalculatorDialogOpen(true),
      icon: CalculatorIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    {
      component: null,
      label: "Print Label",
      description: "",
      path: null,
      action: () => setLabelPrintDialogOpen(true),
      icon: PrinterIcon,
      textColor: "text-black",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
  ];

  const [reportTypeDialogOpen, setReportTypeDialogOpen] = useState(false);

  const handleRetry = useCallback(() => {
    if (!id) return;
    
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: false,
      errors: [],
    }));

    const params = {
      fields: reportListFieldsWithPdf,
    };

    fetchReport(id, params)
      .then((response) => {
        const data = response?.data || response;
        
        if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          toast.error(data.errors[0] || "Something went wrong when trying to load the report");
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            error: true,
            errors: data.errors,
          }));
          return;
        }
        
        if (data?.error) {
          const errorMessage = Array.isArray(data.error) ? data.error : [data.error];
          toast.error(errorMessage[0] || "Something went wrong when trying to load the report");
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            error: true,
            errors: errorMessage,
          }));
          return;
        }

        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          report: data?.data || data,
        }));
      })
      .catch((err) => {
        const errorMessage = err?.errors
          ? (Array.isArray(err.errors) ? err.errors : [err.errors])
          : err?.response?.data?.errors 
          ? (Array.isArray(err.response.data.errors) ? err.response.data.errors : [err.response.data.errors])
          : err?.response?.data?.error
          ? (Array.isArray(err.response.data.error) ? err.response.data.error : [err.response.data.error])
          : err?.error
          ? (Array.isArray(err.error) ? err.error : [err.error])
          : err?.message
          ? [err.message]
          : ["Noe gikk galt ved lasting av rapport"];

        toast.error(errorMessage[0] || "Something went wrong when trying to load the report");
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          error: true,
          errors: errorMessage,
        }));
      });
  }, [id, reportListFieldsWithPdf]);

  if (isLoading) {
    return (
      <DashboardHeader variant="v2" title="Rapport">
        <ResourceLoading />
      </DashboardHeader>
    );
  }

  if (error) {
    return (
      <DashboardHeader variant="v2" title="Rapport">
        <ResourceError errors={errors} onRetry={handleRetry} retryLabel="Last inn på nytt" />
      </DashboardHeader>
    );
  }

  if (!report?.id) {
    return (
      <DashboardHeader variant="v2" title="Rapport">
        <ResourceEmpty />
      </DashboardHeader>
    );
  }

  const reportContent = (
    <>
          <div className="mx-auto max-w-7xl px-4 py-5 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:grid lg:grid-cols-[1fr_18rem] lg:gap-6 lg:pb-5">
            <div className="flex flex-col gap-5 lg:grid lg:grid-cols-5 lg:gap-5 min-w-0">
            {report?.finished && !report?.deleted && (
              <div className="col-span-5 lg:col-span-5">
                <ReportApprovalSection
                  report={report}
                  onRefresh={handleOnRefresh}
                />
              </div>
            )}

            <div className="col-span-5 lg:col-span-5">
              {report?.report_type?.agreement && !report?.deleted && (
                <ReportAgreement report={report} onRefresh={handleOnRefresh} />
              )}
            </div>

            <div className="col-span-5 lg:col-span-5">
              {report?.id && !report?.deleted && (
                <ReportAccessesSection
                  reportId={report.id}
                  participants={report.participants || []}
                  onParticipantsChange={handleParticipantsChange}
                  canManage={canEditReportContent}
                  executingEmployeeId={report.executing_employee?.id}
                />
              )}
            </div>

            {contactId && !report?.deleted && (
              <div className="col-span-5 lg:col-span-5">
                <ContactSection contactId={contactId} mini={true} />
              </div>
            )}

            <div
              className={TWclassNames(
                "col-span-5",
                report?.report_type?.system ? "lg:col-span-2" : "lg:col-span-5"
              )}
            >
              <ReportAddress report={report} />
            </div>

            {report?.report_type?.system && (
              <div className="col-span-5 lg:col-span-3">
                <CustomerSystemSection
                  system={report?.system}
                  customer={report?.customer}
                  module="report"
                  moduleParam={report?.id}
                  onRefresh={handleOnRefresh}
                  operations={report?.operations}
                />
              </div>
            )}

            <div className="col-span-5 lg:col-span-5">
              <section
                className="bg-white shadow sm:rounded-lg"
                aria-labelledby="applicant-information-title"
              >
                <div className="px-4 py-5 sm:px-6">
                  <h2
                    id="applicant-information-title"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Oppdragsbeskrivelse
                  </h2>
                  <p
                    className="italic text-gray-500 hover:text-blue-400 hover:cursor-pointer"
                    onClick={() => setReportTypeDialogOpen(true)}
                  >
                    {report?.report_type?.displayTitle}
                  </p>

                  <ServiceReportTypeConditionsDialog
                    open={reportTypeDialogOpen}
                    onClose={() => setReportTypeDialogOpen(false)}
                    reportType={report?.report_type}
                  />

                  <div className="border-t border-gray-200 mt-4">
                    <div className="py-5">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: report?.description,
                        }}
                        className="text-sm text-gray-900"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                            <TinyDocumentSection
          module="report_description"
          moduleParam={report?.id}
          refreshTrigger={reportVersion}
        />
                  </div>
                </div>
              </section>
            </div>

            <div className="col-span-5 lg:col-span-5">
              <section
                className="bg-white shadow sm:rounded-lg"
                aria-labelledby="applicant-information-title"
              >
                <div className="px-4 py-5 sm:px-6">
                  <h2
                    id="applicant-information-title"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Intern beskrivelse
                  </h2>
                  <div className="border-t border-gray-200 mt-4">
                    <div className="py-5">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: report?.internal_description,
                        }}
                        className="text-sm text-gray-900"
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
            
            <div className="col-span-5 lg:col-span-5">
              <NotesSection module="report" module_param={reportId} />
            </div>

            {/* Report Sections Cards — desktop only; mobile uses bottom nav */}
            {!report?.deleted && (
              <div className="col-span-5 lg:col-span-5 hidden lg:block">
                <div
                  className={`grid grid-cols-1 gap-6 ${(() => {
                    const activeModules = [
                      report?.report_type?.items,
                      report?.report_type?.variables,
                      report?.report_type?.checklist,
                      showReportGuideSection,
                      true, // report is always shown
                    ].filter(Boolean).length;

                    if (activeModules === 1)
                      return "sm:grid-cols-1 lg:grid-cols-1 xl:grid-cols-1";
                    if (activeModules === 2)
                      return "sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2";
                    if (activeModules === 3)
                      return "sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3";
                    if (activeModules === 4)
                      return "sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-4";
                    return "sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5";
                  })()}`}
                >
                  {/* Item Consumption Card */}
                  {report?.report_type?.items && (() => {
                    const orderLinesStatus = ServiceReportOperationObjectStatus({
                      operations: report?.operations,
                      objectName: "order_lines",
                    });
                    const itemConsumptionStatus = ServiceReportOperationObjectStatus({
                      operations: report?.operations,
                      objectName: "item_consumption",
                    });
                    
                    // Check item_consumption status first (takes priority)
                    let statusText = "Mangler";
                    let isCompleted = false;
                    let isRequired = true;
                    
                    if (itemConsumptionStatus === "REQUIRED") {
                      // Item consumption not handled - blocks completion
                      statusText = "Mangler";
                      isCompleted = false;
                      isRequired = true;
                    } else if (itemConsumptionStatus === "FINISHED") {
                      // Item consumption handled - check order_lines status
                      if (orderLinesStatus === "FINISHED") {
                        statusText = "Ferdig";
                        isCompleted = true;
                        isRequired = true;
                      } else if (orderLinesStatus === "NOT_REQUIRED") {
                        statusText = "Ikke påkrevd";
                        isCompleted = true;
                        isRequired = false;
                      } else {
                        statusText = "Mangler";
                        isCompleted = false;
                        isRequired = true;
                      }
                    } else if (itemConsumptionStatus === "NOT_REQUIRED") {
                      // No item consumption operations - use order_lines status
                      if (orderLinesStatus === "FINISHED") {
                        statusText = "Ferdig";
                        isCompleted = true;
                        isRequired = true;
                      } else if (orderLinesStatus === "NOT_REQUIRED") {
                        statusText = "Ikke påkrevd";
                        isCompleted = true;
                        isRequired = false;
                      } else {
                        statusText = "Mangler";
                        isCompleted = false;
                        isRequired = true;
                      }
                    } else {
                      // Fallback to order_lines status
                      if (orderLinesStatus === "FINISHED") {
                        statusText = "Ferdig";
                        isCompleted = true;
                        isRequired = true;
                      } else if (orderLinesStatus === "NOT_REQUIRED") {
                        statusText = "Ikke påkrevd";
                        isCompleted = true;
                        isRequired = false;
                      } else {
                        statusText = "Mangler";
                        isCompleted = false;
                        isRequired = true;
                      }
                    }
                    
                    return (
                      <ReportSectionCard
                        title="Vareforbruk"
                        description="Registrer forbruk av materialer og varer"
                        icon={CubeIcon}
                        count={
                          report?.items_summary?.count ??
                          report?.items?.filter((item) => !item.delete).length ??
                          0
                        }
                        unit="varer"
                        statusText={statusText}
                        isCompleted={isCompleted}
                        isRequired={isRequired}
                        canEdit={canEditReportContent}
                        to={`/dashboard/service/reports/${report?.id}/item-consumption`}
                        footerExtra={
                          report?.item_list_product_status &&
                          report.item_list_product_status.lines_total > 0 ? (
                            <ItemListProductStatusTag
                              status={report.item_list_product_status}
                              compact
                            />
                          ) : null
                        }
                      />
                    );
                  })()}

                  {/* Variables Card */}
                  {report?.report_type?.variables && (
                    <ReportSectionCard
                      title="Variabler"
                      description="Registrer variabler for rapporten"
                      icon={VariableIcon}
                      count={report?.variables?.filter((variable) => !variable.delete).length || 0}
                      unit="variabler"
                      statusText={
                        ServiceReportOperationObjectStatus({
                          operations: report?.operations,
                          objectName: "variables",
                        }) === "FINISHED"
                          ? "Ferdig"
                          : ServiceReportOperationObjectStatus({
                              operations: report?.operations,
                              objectName: "variables",
                            }) === "NOT_REQUIRED"
                          ? "Ikke påkrevd"
                          : "Mangler"
                      }
                      isCompleted={
                        ServiceReportOperationObjectStatus({
                          operations: report?.operations,
                          objectName: "variables",
                        }) === "FINISHED"
                      }
                      isRequired={
                        ServiceReportOperationObjectStatus({
                          operations: report?.operations,
                          objectName: "variables",
                        }) !== "NOT_REQUIRED"
                      }
                      canEdit={canEditReportContent}
                      {...(variablesOpenLegacyDialog
                        ? {
                            onClick: () => {
                              setSelectedSection("variables");
                              setSectionDialogOpen(true);
                            },
                          }
                        : {
                            to:
                              reportVariablesHref ||
                              `/dashboard/service/reports/${report?.id}/variables`,
                          })}
                    />
                  )}

                  {/* Checklist Card */}
                  {report?.report_type?.checklist && (() => {
                    // Report payload is refreshed after edits; local checklist state is only set on first load.
                    const checklistData = report?.checklist?.id
                      ? report.checklist
                      : checklist;
                    const checklistStatus = getChecklistStatus(checklistData);
                    
                    return (
                      <ReportSectionCard
                        title="Sjekkliste"
                        description={
                          checklistData?.id
                            ? "Fullfør sjekkliste for rapporten"
                            : "Velg sjekkliste type for å knytte til rapporten"
                        }
                        icon={ClipboardDocumentIcon}
                        count={checklistStatus.completedCount}
                        total={checklistStatus.totalCount}
                        unit="punkter"
                        statusText={checklistStatus.statusText}
                        isCompleted={checklistStatus.isCompleted}
                        isRequired={checklistStatus.isRequired}
                        showProgress={checklistData?.id}
                        progressValue={checklistStatus.completedCount}
                        progressMax={checklistStatus.totalCount}
                        canEdit={canEditReportContent}
                        to={`/dashboard/service/reports/${report?.id}/checklist`}
                      />
                    );
                  })()}

                  {/* Report guides — desktop summary + history */}
                  {showReportGuideSection && (
                    <ReportSectionCard
                      title={t("report.view.sectionGuide.title")}
                      description={t("report.view.sectionGuide.description")}
                      icon={SparklesIcon}
                      count={reportGuideRuns.length}
                      unit={t("report.view.sectionGuide.unitRuns")}
                      statusText={
                        reportGuideRuns.length > 0
                          ? t("report.view.sectionGuide.statusHasRuns", {
                              count: reportGuideRuns.length,
                            })
                          : t("report.view.sectionGuide.statusNone")
                      }
                      contentPreview={
                        reportGuideRuns[0]?.template?.title
                          ? `${reportGuideRuns[0].template.title}${
                              reportGuideRuns[0]?.completed_at ||
                              reportGuideRuns[0]?.updated_at
                                ? ` · ${new Date(
                                    reportGuideRuns[0].completed_at ||
                                      reportGuideRuns[0].updated_at
                                  ).toLocaleString(undefined, {
                                    dateStyle: "short",
                                    timeStyle: "short",
                                  })}`
                                : ""
                            }`
                          : ""
                      }
                      isCompleted={reportGuideRuns.length > 0}
                      isRequired={false}
                      canEdit={canEditReportContent}
                      onClick={() => setReportGuideHistoryOpen(true)}
                    />
                  )}

                  {/* Report Content Card - Always shown */}
                  <ReportSectionCard
                    title={t("report.view.sectionReport.title")}
                    description={t("report.view.sectionReport.description")}
                    contentPreview={reportSectionContentPreview}
                    icon={ClipboardDocumentCheckIcon}
                    count={report?.report ? 1 : 0}
                    total={1}
                    unit={t("report.view.sectionReport.unit")}
                    statusText={
                      ServiceReportOperationObjectStatus({
                        operations: report?.operations,
                        objectName: "report",
                      }) === "FINISHED"
                        ? t("report.view.sectionReport.statusFinished")
                        : ServiceReportOperationObjectStatus({
                            operations: report?.operations,
                            objectName: "report",
                          }) === "NOT_REQUIRED"
                        ? t("report.view.sectionReport.statusNotRequired")
                        : t("report.view.sectionReport.statusMissing")
                    }
                    isCompleted={
                      ServiceReportOperationObjectStatus({
                        operations: report?.operations,
                        objectName: "report",
                      }) === "FINISHED"
                    }
                    isRequired={true}
                    canEdit={canEditReportContent}
                    onClick={() => {
                      setSelectedSection("report");
                      setSectionDialogOpen(true);
                    }}
                  />
                </div>
              </div>
            )}

            {/* Time Registration Section */}
            {!report?.deleted && (
              <div
                id="report-view-time-registration"
                className="col-span-5 lg:col-span-5 scroll-mt-20"
                data-section="time-registration"
              >
                <TimeRegistrationSection
                  module="report"
                  moduleId={reportId}
                  project={report?.project}
                  report={report}
                  onRefresh={handleOnRefresh}
                  title="Timeregistrering"
                  description="Registrer timer for denne rapporten"
                  openRegistrationDialogSignal={timeRegistrationDialogSignal}
                />
              </div>
            )}

            {/* Documents Section */}
            {!report?.deleted && (
              <div className="col-span-5 lg:col-span-5">
                <DocumentManagerV6
                  module="report"
                  moduleParam={report?.id}
                  mode="dropzone"
                  maxDisplay={12}
                  showUpload={canEditReportContent}
                  showExisting={true}
                  allowDelete={canEditReportContent}
                  allowEdit={canEditReportContent}
                  allowCopy={canEditReportContent}
                  allowMove={canEditReportContent}
                  autoLoad={false}
                  // disabled={report?.user_can_edit === false}
                  onDocumentsChange={handleDocumentsChange}
                />
              </div>
            )}

            {/* Finish Report Section */}
            {!report?.deleted && (
              <div className="col-span-5 lg:col-span-5">
                <div
                  className={`bg-white rounded-lg border-2 shadow-sm ${
                    report?.finished
                      ? "border-green-200"
                      : getFinishReportProgress().isComplete
                      ? "border-green-200"
                      : "border-yellow-200"
                  }`}
                >
                  <div
                    className={`px-6 py-4 border-b ${
                      report?.finished
                        ? "border-green-200 bg-green-50"
                        : getFinishReportProgress().isComplete
                        ? "border-green-200 bg-green-50"
                        : "border-yellow-200 bg-yellow-50"
                    }`}
                  >
                    <h3
                      className={`text-lg font-semibold ${
                        report?.finished
                          ? "text-green-900"
                          : getFinishReportProgress().isComplete
                          ? "text-green-900"
                          : "text-yellow-900"
                      }`}
                    >
                      {report?.finished
                        ? "Rapport ferdigstilt"
                        : "Ferdigstill rapport"}
                    </h3>
                    <p
                      className={`text-sm mt-1 ${
                        report?.finished
                          ? "text-green-700"
                          : getFinishReportProgress().isComplete
                          ? "text-green-700"
                          : "text-yellow-700"
                      }`}
                    >
                      {report?.finished
                        ? "Rapporten er ferdigstilt og sendt til kunde"
                        : "Sjekk og ferdigstill rapporten når alle seksjoner er ferdig"}
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {report?.finished ? "Status" : "Fremgang"}
                        </span>
                        <span className="text-sm text-gray-500">
                          {report?.finished
                            ? "Ferdigstilt"
                            : (() => {
                                const progress = getFinishReportProgress();
                                return `${progress.completed} / ${progress.total}`;
                              })()}
                        </span>
                      </div>
                      {!report?.finished && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${Math.min(
                                (() => {
                                  const progress = getFinishReportProgress();
                                  return (
                                    (progress.completed /
                                      Math.max(progress.total, 1)) *
                                    100
                                  );
                                })(),
                                100
                              )}%`,
                            }}
                          ></div>
                        </div>
                      )}
                      {report?.finished && (
                        <div className="w-full bg-green-200 rounded-full h-2">
                          <div className="bg-green-600 h-2 rounded-full"></div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={() => setReportFinishDialogOpen(true)}
                        className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                      >
                        {report?.finished
                          ? "Se ferdigstilt rapport"
                          : "Ferdigstill rapport"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(() => {
              const calcEntries =
                report?.additional_work_calculator_data?.entries;
              const last =
                Array.isArray(calcEntries) && calcEntries.length > 0
                  ? calcEntries[0]
                  : null;
              if (!last?.totals || report?.deleted) return null;
              return (
                <div className="col-span-5 lg:col-span-5">
                  <div className="rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/90 to-white px-4 py-4 sm:px-5 sm:py-5 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800/80">
                      {t("report.view.priceCalculated.title")}
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      {t("report.view.priceCalculated.subtitle")}
                    </p>
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                      <div className="grid grid-cols-2 gap-4 sm:gap-8">
                        <div>
                          <p className="text-xs text-slate-500">
                            {t("report.view.priceCalculated.exVat")}
                          </p>
                          <p className="text-lg font-semibold tabular-nums text-slate-900">
                            kr{" "}
                            {Math.round(
                              Number(last.totals.customer_total_ex_vat) || 0
                            ).toLocaleString("nb-NO")}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">
                            {t("report.view.priceCalculated.incVat")}
                          </p>
                          <p className="text-lg font-semibold tabular-nums text-emerald-800">
                            kr{" "}
                            {Math.round(
                              Number(last.totals.customer_total_inc_vat) || 0
                            ).toLocaleString("nb-NO")}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCalculatorDialogOpen(true)}
                        className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 w-full sm:w-auto"
                      >
                        {t("report.view.priceCalculated.open")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Report Summary Section - Admin Only */}
            {hasAdminAccess && !report?.deleted && (
              <div className="col-span-5 lg:col-span-5">
                <ReportSummarySection
                  report={report}
                  summary={reportFinancialSummary}
                  summaryLoading={reportFinancialSummaryLoading}
                />
              </div>
            )}
            </div>

            {/* Admin- og Dev-menyer - kun synlige på lg+ for service reset-tilgang */}
            <aside className="hidden lg:flex lg:flex-col lg:gap-4">
              <ReportAdminMenu
                report={report}
                isLoading={isLoading}
                onRefresh={handleOnRefresh}
              />
              <ReportDevMenu report={report} />
            </aside>
          </div>

          {!report?.deleted && (
            <nav
              className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/90"
              style={{
                paddingBottom: "max(0.35rem, env(safe-area-inset-bottom, 0px))",
              }}
              aria-label={t("report.view.mobileNav.ariaLabel")}
            >
              <div className="flex items-stretch justify-around gap-0 px-0.5 pt-1">
                {report?.report_type?.items && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/dashboard/service/reports/${report.id}/item-consumption`
                      )
                    }
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                  >
                    <CubeIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.items")}
                    </span>
                  </button>
                )}
                {report?.report_type?.variables && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        reportVariablesHref ||
                          `/dashboard/service/reports/${report.id}/variables`
                      )
                    }
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                  >
                    <VariableIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.variables")}
                    </span>
                  </button>
                )}
                {report?.report_type?.checklist && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/dashboard/service/reports/${report.id}/checklist`
                      )
                    }
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                  >
                    <ClipboardDocumentIcon
                      className="h-5 w-5 shrink-0"
                      aria-hidden
                    />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.checklist")}
                    </span>
                  </button>
                )}
                {showReportGuideSection && (
                  <button
                    type="button"
                    onClick={openGuideEntryPoint}
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                  >
                    <SparklesIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.guide")}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSection("report");
                    setSectionDialogOpen(true);
                  }}
                  className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                >
                  <ClipboardDocumentCheckIcon
                    className="h-5 w-5 shrink-0"
                    aria-hidden
                  />
                  <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                    {t("report.view.mobileNav.report")}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setTimeRegistrationDialogSignal((n) => n + 1)
                  }
                  className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                >
                  <ClockIcon className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                    {t("report.view.mobileNav.timeRegistration")}
                  </span>
                </button>
              </div>
            </nav>
          )}
        </>
  );

  return (
    <>
      <DashboardHeader
        variant="v2"
        subtitle=""
        actions={headerActions}
        title={FullReportTitle(report)}
        meta={headerMeta}
        summaryItems={summaryItems}
        backUrl="/dashboard/service/reports"
        mainButton={{
          label: "Edit",
          action: () => setReportDialogOpen(true),
          path: null,
          color: "",
          icon: PencilIcon,
        }}
      >
        {reportContent}
      </DashboardHeader>

      <HistoriesModal
        open={headerHistoriesOpen}
        onClose={() => setHeaderHistoriesOpen(false)}
        module="report"
        module_param={report?.id}
      />

      {hasAdminAccess && report?.customer?.id ? (
        <CommuncationsSection
          customer={report.customer}
          related_type="report"
          related_id={report?.id}
          type="button"
          renderTrigger={false}
          open={headerCommunicationsOpen}
          onOpenChange={setHeaderCommunicationsOpen}
        />
      ) : null}

      <ReportSendDialog
        report={memoizedReport}
        open={reportSendDialogOpen}
        isLoading={isLoading}
        onClose={() => setReportSendDialogOpen(false)}
        onRefresh={handleOnRefresh}
      />

      <ReportPrintDialog
        report={memoizedReport}
        open={reportPrintDialogOpen}
        isLoading={isLoading}
        onClose={() => setReportPrintDialogOpen(false)}
        onRefresh={handleOnRefresh}
      />

      <ReportResetDialog
        report={memoizedReport}
        open={reportResetDialogOpen}
        isLoading={isLoading}
        onClose={() => setReportResetDialogOpen(false)}
        onRefresh={handleOnRefresh}
      />

      <ReportExecutiveDialog
        report={memoizedReport}
        open={reportExecutiveDialogOpen}
        isLoading={isLoading}
        onClose={() => setReportExecutiveDialogOpen(false)}
        onRefresh={handleOnRefresh}
      />

      <ReportOrderDialog
        report={memoizedReport}
        open={reportOrderDialogOpen}
        isLoading={isLoading}
        onClose={() => setReportOrderDialogOpen(false)}
        onRefresh={handleOnRefresh}
      />

              <ServiceReportEditModal
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          selected={memoizedReport}
          onRefresh={handleOnRefresh}
          onDocumentRefresh={() => setReportVersion(prev => prev + 1)}
        />

      <ReportCopyModal
        open={reportCopyDialogOpen}
        onClose={() => setReportCopyDialogOpen(false)}
        report={memoizedReport}
      />

      <LabelPrintDialog
        open={labelPrintDialogOpen}
        isLoading={isLoading}
        onClose={() => setLabelPrintDialogOpen(false)}
        title={report?.report_number}
        subtitle={
          report?.report_type?.displayTitle + " - " + report?.customer?.name
        }
        description={report?.delivery_address?.displayTitle}
        qr_code={`https://promang.no/dashboard/service/reports/${report?.id}`}
      />

      <ReportSectionDialog
        open={sectionDialogOpen}
        onClose={() => {
          // If checklist dialog was open, refresh checklist status
          // if (selectedSection === "checklist" && report?.checklist?.id) {
          //   loadChecklist(report.id);
          // }
          
          setSectionDialogOpen(false);
          setSelectedSection(null);
        }}
        report={memoizedReport}
        section={selectedSection}
        onRefresh={handleOnRefresh}
      />


      <ReportFinishDialog
        open={reportFinishDialogOpen}
        onClose={() => setReportFinishDialogOpen(false)}
        selected={memoizedReport}
        onRefresh={(data) => {
          console.log("data from FinishDialog", data);
          handleOnRefresh(data.data);
          setReportFinishDialogOpen(false);
        }}
        isLoading={isLoading}
      />

      <ReportAdditionalWorkCalculator
        report={memoizedReport}
        onRefresh={handleOnRefresh}
        open={calculatorDialogOpen}
        onClose={() => setCalculatorDialogOpen(false)}
        showSection={false}
      />

      <PMDialog
        open={reportGuideChooserState.open}
        onClose={closeReportGuideChooser}
        title={t("Velg rapportguide")}
        size="3xl"
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            {t("Velg guiden som passer best med oppdraget.")}
          </p>
          {(reportGuideChooserState.templates || []).map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => {
                closeReportGuideChooser();
                startReportGuide(template);
              }}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left transition hover:border-sky-300 hover:bg-sky-50"
            >
              <div className="text-sm font-semibold text-slate-950">
                {template.title || t("Rapportguide")}
              </div>
              {template.description ? (
                <div className="mt-1 text-sm text-slate-500">{template.description}</div>
              ) : null}
              <div className="mt-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                {(template.questions || []).length} {t("spørsmål")}
              </div>
            </button>
          ))}
        </div>
      </PMDialog>

      <ReportGuideOnboardingDialog
        open={reportGuideState.open}
        template={reportGuideState.template}
        report={memoizedReport}
        isSaving={reportGuideState.isSaving}
        onClose={closeReportGuide}
        onComplete={completeReportGuide}
      />

      <ReportGuideHistoryDialog
        open={reportGuideHistoryOpen}
        onClose={() => setReportGuideHistoryOpen(false)}
        runs={reportGuideRuns}
        canStartMore={canEditReportContent && reportGuideTemplates.length > 0}
        onStartGuide={openReportGuideChooser}
      />
    </>
  );
};
